import { hasTaskWriteProof, isTaskWriteAction, parseTaskActionRequest, sameReviewedTaskRequest, taskId } from "../../supabase/functions/_shared/taskActionDomain";
import type { TaskResponse } from "./taskExecutor";

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/** A local journal is only a locator; the owned receipt must bind the exact reviewed request. */
function boundReceipt(body: Record<string, unknown>, value: unknown, ownerId?: string): value is TaskResponse {
  try {
    const reviewed = parseTaskActionRequest(body);
    if (!isTaskWriteAction(reviewed.action) || !object(value) || value.ok !== true || value.action !== "receipt"
      || value.operation_id !== reviewed.operation_id || value.receipt_action !== reviewed.action
      || value.task_id !== reviewed.task_id || taskId(value.receipt_id) !== value.receipt_id
      || !sameReviewedTaskRequest(reviewed, value.request)) return false;
    const stored = parseTaskActionRequest(value.request);
    return !ownerId || !stored.expected_snapshot || stored.expected_snapshot.task.user_id === ownerId;
  } catch { return false; }
}

export function hasConfirmedTaskReceiptResult(body: Record<string, unknown>, value: unknown, ownerId?: string): value is TaskResponse {
  return boundReceipt(body, value, ownerId) && value.status === "succeeded"
    && value.safe_retry !== true && value.error_code == null
    && value.error == null && hasTaskWriteProof(body, value.result, ownerId);
}

export function isConfirmedTaskReceiptFailure(body: Record<string, unknown>, value: unknown, ownerId?: string): boolean {
  return boundReceipt(body, value, ownerId) && value.status === "failed" && value.safe_retry === true
    && value.result === null && typeof value.error_code === "string" && value.error_code.length > 0;
}

/** Missing is a current owned lookup, not permission to replay an old write automatically. */
export function isMissingTaskReceipt(body: Record<string, unknown>, value: unknown): boolean {
  try {
    const request = parseTaskActionRequest(body);
    return isTaskWriteAction(request.action) && object(value) && value.ok === true && value.action === "receipt"
      && value.operation_id === request.operation_id && value.status === "missing" && value.safe_retry === true
      && Object.keys(value).every(key => ["ok", "action", "operation_id", "status", "safe_retry"].includes(key));
  } catch { return false; }
}
